import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStatus } from "../Auth/AuthStatusContext";
import { useAuth } from 'react-oidc-context';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  ExitToApp as ExitToAppIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Description as TemplatesIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';

// In Vite, public assets are accessed directly from the root
const logoUrl = '/logo512.png';

const TitleBar: React.FC = () => {
  const navigate = useNavigate();
  const { profile, logout } = useAuthStatus();
  const auth = useAuth();

  // Get the user's email from the profile or auth user info
  const userEmail = profile?.email || auth.user?.profile?.email || 'User';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = () => {
    // Call the logout function from UserContext which handles token removal
    logout();
    // Close the menu
    handleClose();
    // Don't navigate here - the redirect will happen automatically from the signoutRedirect
  };

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  return (
    <>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ minHeight: '70px', height: '70px' }}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ mr: 2 }}
            data-testid="menu-button"
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={logoUrl}
                alt="MentoringFusion Logo"
                style={{
                  height: 50,
                  width: 50,
                  marginRight: 12
                }}
              />
              <Typography
                variant="h5"
                component={Link}
                to="/"
                sx={{
                  flexGrow: 1,
                  textDecoration: 'none',
                  color: 'white',
                  fontWeight: 'bold',
                  letterSpacing: '0.5px'
                }}
                data-testid="title-link"
              >
                MentoringFusion
              </Typography>
            </Box>
          </Box>

          {auth.isAuthenticated && (
            <div>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ display: 'flex', alignItems: 'center' }}
                data-testid="account-button"
              >
                <AccountCircleIcon />
                <Typography
                  component="span"
                  noWrap
                  variant="subtitle2"
                  sx={{
                    ml: 1,
                    maxWidth: 200,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    display: 'inline-block'
                  }}
                >
                  {userEmail as React.ReactNode}
                </Typography>
              </IconButton>

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => handleMenuClick('/profile')}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>

                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <ExitToAppIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(90deg, #2B8EF8 0%, #9C45F3 100%)',
            color: 'white'
          }}>
            <img src={logoUrl} alt="MentoringFusion Logo" style={{ height: 36, width: 36 }} />
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 'bold' }}>MentoringFusion</Typography>
          </Box>

          <List sx={{ flexGrow: 1, pt: 0 }}>
            <ListItem button component={Link} to="/" onClick={toggleDrawer(false)}>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                secondary="View your campaigns"
              />
            </ListItem>

            <Divider />

            <ListItem button component={Link} to="/application-templates" onClick={toggleDrawer(false)}>
              <ListItemIcon>
                <TemplatesIcon />
              </ListItemIcon>
              <ListItemText
                primary="Application Templates"
                secondary="Create and manage custom templates"
              />
            </ListItem>

            <Divider />

            <ListItem button component={Link} to="/campaigns" onClick={toggleDrawer(false)}>
              <ListItemIcon>
                <CampaignIcon />
              </ListItemIcon>
              <ListItemText
                primary="Campaigns"
                secondary="Manage your campaigns"
              />
            </ListItem>

            <ListItem button component={Link} to="/organizations" onClick={toggleDrawer(false)}>
              <ListItemIcon>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText
                primary="Organizations"
                secondary="Manage your organizations"
              />
            </ListItem>

            <Divider />

            <ListItem button component={Link} to="/training-resources" onClick={toggleDrawer(false)}>
              <ListItemIcon>
                <SchoolIcon />
              </ListItemIcon>
              <ListItemText
                primary="Training Resources"
                secondary="Access training materials"
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default TitleBar;